import { Flex, Grid, View, minmax, repeat } from '@adobe/react-spectrum';
import { error } from '@react/react-spectrum/Toast';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { commonApi, excApi, handleApiCall } from '@exchange-frontends/api';
import { CloudAppCard } from '@exchange-frontends/components';
import { useStaticData } from '@exchange-frontends/custom-hooks';

import { DetailHeading } from '../../../../components/DetailHeading';
import { ContainerContext } from '../../../../utils';
import { useStore } from '../../store';
import { ConfigContext } from '../App';
import { AppDetailsContext } from './AppDetails';
import ViewAllLink from './ViewAllLink';

const DEFAULT_SHOWN_APPS = 2;

/**
 * More By Publisher that shows first two apps related to the app
 * @component
 * @category Publisher apps
 */
const PublisherApps = () => {
  const { host = '' } = useContext(ConfigContext) ?? {};
  const { id } = useParams();
  const { cloud } = useContext(AppDetailsContext);
  const { ims } = useContext(ContainerContext);
  const accessToken = useMemo(() => ims?.getAccessToken()?.token, [ims]);
  const detailScreen = useStore((state) => state.detailScreen);
  const publisherId = detailScreen?.publisher?.id;
  const appType = detailScreen?.appType;
  /** Loader for  Apps by publisher since its a seperate API call */
  const [loading, setLoading] = useState(true);
  const productsLocs = useStaticData('hostApps');
  /** @type {ReturnType<typeof useState<number | undefined>>} */
  const [pageSize, setPageSize] = useState();
  const [data, setData] = useState({ cloudApps: Array(DEFAULT_SHOWN_APPS).fill(null) });
  const [showAllApps, setShowAllApps] = useState(false);

  const apps = data?.cloudApps;
  const totalApps = data?._metadata?.totalCount || data?.totalCount;
  const shownAppsAmount = showAllApps ? totalApps : DEFAULT_SHOWN_APPS;

  // Fetch publisher apps
  useEffect(() => {
    let mounted = true;
    const params = {
      resultType: 'card',
      publisherId,
      authToken: accessToken,
      pageSize,
    };
    if (appType === 'SLN') {
      params.appType = appType;
    }

    let apiCallFunction;
    if (cloud === 'cc') {
      if (appType === 'integrations') {
        apiCallFunction = excApi.getPublisherIntegrations;
      } else {
        apiCallFunction = commonApi.ccesV3Extensions;
      }
    } else {
      apiCallFunction = excApi.getPublisherApps;
    }

    if (apiCallFunction !== excApi.getPublisherIntegrations) {
      // 3 in case the current app is in the list
      params.pageSize = 3;
      params.sort = 'MOST_RECENT';
    }

    const handler = handleApiCall(apiCallFunction, params);

    if (publisherId) {
      setLoading(true);
      setData({ cloudApps: Array(pageSize || DEFAULT_SHOWN_APPS).fill(null) });
      handler
        .run()
        .then((data) => {
          if (mounted && data?.cloudApps) {
            data.cloudApps = data.cloudApps?.filter((app) => {
              return id !== String(app.id);
            });
            setData(data);
          }
        })
        .catch((err) => {
          // CCES asked us to redirect to login on 429
          if (err.code === 429) {
            if (!ims?.isMock) {
              ims.signIn();
            } else {
              error('You need to login in order to continue', { timeout: 0 });
            }
            // 20 is the error code for aborted requests
          } else if (err.code !== 20) {
            setData({ cloudApps: [] });
            console.error(err);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
    return () => {
      handler.controller.abort();
      mounted = false;
    };
  }, [accessToken, appType, cloud, id, ims, pageSize, publisherId]);

  const heading = `More by this ${appType === 'SLN' ? 'Partner' : 'Publisher'}`;

  return (
    <Flex alignItems={'stretch'} direction="column" gridArea="moreApps">
      <View>
        <Flex justifyContent="space-between" alignItems="center">
          {apps?.length >= 1 && <DetailHeading level={4}>{heading}</DetailHeading>}
          {totalApps > 3 && appType !== 'integrations' && (
            <ViewAllLink
              showAllApps={showAllApps}
              onViewLess={() => setShowAllApps(false)}
              onViewAll={() => {
                setShowAllApps(true);
                setPageSize(totalApps);
              }}
            />
          )}
        </Flex>
        <Grid gap="size-200" columns={repeat('auto-fill', minmax('min(100%, max(270px, 100%/2.5))', '1fr'))}>
          {apps?.slice(0, shownAppsAmount).map((app, key) => (
            <CloudAppCard
              key={app ? `${app?.id}.${app?.name}.${app?.type}` : key}
              headerId="MORE BY THIS PUBLISHER"
              cloud={cloud}
              app={app}
              loading={loading || !app}
              productsLocs={productsLocs}
              host={host}
            />
          ))}
        </Grid>
      </View>
    </Flex>
  );
};

export default PublisherApps;
